/* Jam chart: a song as a timeline of chord notes (one per bar) in seconds from its first downbeat,
   with the lanes the highway draws, section starts and timed lyrics. */
import { type ChordRef, isSingleNote, matchChord } from '../dsp/chord';
import { parseChord } from '../theory/chords';
import { type LyricLine, type TimedLine, timeLyrics } from './lyrics';
import type { HeardChord } from './score';

/** What the jam needs from a song (a PreparedSong fits). */
export interface JamSong {
  bpm: number;
  beats: number;
  bars: readonly { chord: string; sec: number }[];
  sections: readonly { name: string }[];
  lyrics?: readonly LyricLine[];
}

export interface JamNote {
  bar: number;
  chord: string;
  ref: ChordRef;
  sec: number;
  /** highway lane: index into JamChart.lanes */
  lane: number;
  /** seconds from the first downbeat */
  t: number;
  end: number;
}

export interface JamChart {
  bpm: number;
  /** seconds per beat */
  beat: number;
  beats: number;
  barLen: number;
  /** count-in before the first downbeat, in seconds and in beats */
  countIn: number;
  countBeats: number;
  /** seconds from the first downbeat to the end of the last bar */
  length: number;
  notes: JamNote[];
  /** distinct chords in order of first appearance, one lane each */
  lanes: string[];
  sections: { name: string; bar: number; t: number }[];
  lyrics: TimedLine[];
}

/** Chords the microphone listens for: every major and minor triad plus the song's own chords. */
export function jamVocab(chart: Pick<JamChart, 'notes'>): ChordRef[] {
  const out = new Map<string, ChordRef>();
  for (let root = 0; root < 12; root++) for (const q of ['', 'm'] as const) out.set(`${root}|${q}`, { root, q });
  for (const n of chart.notes) out.set(`${n.ref.root}|${n.ref.q}`, n.ref);
  return Array.from(out.values());
}

/** Power chords on every root: two strings already make a chord, so they are never taken for one note. */
const POWER: readonly ChordRef[] = Array.from({ length: 12 }, (_, root) => ({ root, q: '5' as const }));

/** What the microphone hears in a chord frame: the best chord of `vocab`, or null when the frame is one
    note. A plucked, sung or hummed note reads as the major chord on it (its harmonics), and one note is
    not a chord (isSingleNote). */
export function chordHearing(vocab: readonly ChordRef[]): (chroma: Float64Array, bass: number) => HeardChord | null {
  const notes = [...vocab, ...POWER];
  return (c, bass) => (isSingleNote(c, notes) ? null : (matchChord(c, bass, vocab)[0] ?? null));
}

/** `tempo` scales the song's own BPM (0.7 = 70 %). */
export function buildChart(song: JamSong, tempo = 1): JamChart {
  const bpm = song.bpm * tempo;
  const beat = 60 / bpm;
  const barLen = beat * song.beats;
  const lanes: string[] = [];
  const notes: JamNote[] = song.bars.map((b, i) => {
    const ref = parseChord(b.chord);
    if (!ref) throw new Error(`Geçersiz akor: ${b.chord}`);
    if (!lanes.includes(b.chord)) lanes.push(b.chord);
    return {
      bar: i,
      chord: b.chord,
      ref: { root: ref.root, q: ref.q },
      sec: b.sec,
      lane: lanes.indexOf(b.chord),
      t: i * barLen,
      end: (i + 1) * barLen,
    };
  });
  const sections: JamChart['sections'] = [];
  notes.forEach((n) => {
    if (n.bar === 0 || n.sec !== notes[n.bar - 1]?.sec)
      sections.push({ name: song.sections[n.sec]?.name ?? '', bar: n.bar, t: n.t });
  });
  // short bars (fast waltzes) get two bars of count-in so the player has time to get ready
  const countBars = barLen < 2 ? 2 : 1;
  return {
    bpm,
    beat,
    beats: song.beats,
    barLen,
    countIn: countBars * barLen,
    countBeats: countBars * song.beats,
    length: notes.length * barLen,
    notes,
    lanes,
    sections,
    lyrics: timeLyrics(song.lyrics ?? [], beat, song.beats),
  };
}
